import React, { useEffect, useRef, useState } from 'react';

import { KeyboardKeys } from '../../assets/enums/keyboard-keys.enum';

import styles from './EditableText.module.scss';

// Builds the text that `node` would hold if `insertedKey` were typed at the current cursor
// position (or selection). Mirrors the selection-slicing `handlePaste` already does below, so a
// `max` check can test the prospective value instead of the stale pre-keystroke ref.
const getProspectiveValue = (node: HTMLDivElement, insertedKey: string): string => {
  const text = node.innerText;
  const selection = window.getSelection();
  const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : undefined;
  const start = range?.startOffset ?? text.length;
  const end = range?.endOffset ?? text.length;

  return `${text.slice(0, start)}${insertedKey}${text.slice(end)}`;
};

interface EditableTextProps {
  onSubmit: (title: string) => void;
  onlyNumber?: boolean;
  defaultText?: string;
  maxLength?: number;
  max?: number;
  min?: number;
  className?: string;
  children: React.ReactNode;
}

export const EditableText: React.FC<EditableTextProps> = ({
  onSubmit,
  defaultText = '-',
  onlyNumber = false,
  maxLength,
  max,
  min,
  className,
  children,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [editModeState, setEditModeState] = useState(false);
  const [initialValue, setInitialValue] = useState(defaultText);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    setInitialValue(textRef?.current?.innerText || defaultText);

    if (textRef && textRef.current) {
      textRef.current.contentEditable = 'true';
    }
  }, [defaultText]);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    setEditModeState(true);

    if (textRef && textRef.current) {
      if (textRef.current.innerText === defaultText) {
        textRef.current.innerText = '';
      }

      textRef.current.contentEditable = 'true';
      textRef.current.focus();
    }
  };

  const handleBlur = async () => {
    setEditModeState(false);

    if (textRef && textRef.current) {
      if (maxLength && textRef.current.innerText.length > maxLength) {
        textRef.current.innerText = initialValue || defaultText;
        setErrorMessage('');
      } else if (textRef.current.innerText === '') {
        textRef.current.innerText = defaultText;
      } else if (onlyNumber && min !== undefined && Number(textRef.current.innerText) < min) {
        // min is a commit-time constraint: typing toward a valid value passes through
        // intermediate values below min (e.g. "" then "1" while typing "10"), so it cannot be
        // enforced per keystroke. Revert to the last committed value instead of submitting.
        textRef.current.innerText = initialValue || defaultText;
      } else {
        await onSubmit(textRef.current!.innerText);
        setInitialValue(textRef.current.innerText);
      }
    }
  };

  const onKeyUp = () => {
    if (maxLength && textRef.current!.innerText.length >= maxLength) {
      setErrorMessage(`Field must be ${maxLength} characters at most`);
    } else {
      setErrorMessage('');
    }
  };

  const onKeyDown = async (e: React.KeyboardEvent) => {
    e.stopPropagation();

    if (!editModeState) {
      return;
    }

    if (
      maxLength &&
      textRef.current!.innerText.length >= maxLength &&
      e.keyCode !== KeyboardKeys.BACKSPACE &&
      e.keyCode !== KeyboardKeys.ARROW_LEFT &&
      e.keyCode !== KeyboardKeys.ARROW_RIGHT
    ) {
      e.preventDefault();
    }

    if (onlyNumber) {
      if (
        !(e.keyCode === KeyboardKeys.BACKSPACE) &&
        !(e.keyCode === KeyboardKeys.ARROW_LEFT) &&
        !(e.keyCode === KeyboardKeys.ARROW_RIGHT) &&
        isNaN(Number(e.key))
      ) {
        e.preventDefault();
      } else if (
        max !== undefined &&
        e.keyCode !== KeyboardKeys.BACKSPACE &&
        e.keyCode !== KeyboardKeys.ARROW_LEFT &&
        e.keyCode !== KeyboardKeys.ARROW_RIGHT &&
        textRef.current
      ) {
        // max is checked prospectively: what the value would become after this keystroke is
        // inserted at the current cursor position, not the stale pre-keystroke ref value (which
        // would otherwise re-trigger on every subsequent keydown once already over max).
        const prospectiveValue = getProspectiveValue(textRef.current, e.key);

        if (prospectiveValue !== '' && Number(prospectiveValue) > max) {
          e.preventDefault();
        }
      }

      // min is enforced at commit time (blur / Enter), not per keystroke — see there.
    }

    if (e.key === KeyboardKeys.Escape) {
      Promise.resolve().then(() => {
        setEditModeState(false);

        if (textRef && textRef.current) {
          textRef.current.innerText = initialValue || defaultText;
          setErrorMessage('');
          textRef.current.contentEditable = 'false';
          textRef.current.contentEditable = 'true';
        }
      });
    }

    if (e.key === KeyboardKeys.Enter) {
      e.preventDefault();

      setEditModeState(false);

      if (textRef && textRef.current) {
        textRef.current.contentEditable = 'false';
        textRef.current.contentEditable = 'true';

        if (!onlyNumber) {
          setInitialValue(textRef.current.innerText);
        }

        if (maxLength && textRef.current.innerText.length > maxLength) {
          textRef.current.innerText = initialValue || defaultText;
          setErrorMessage('');
        } else if (textRef.current.innerText === '') {
          textRef.current.innerText = defaultText;
        } else if (onlyNumber && min !== undefined && Number(textRef.current.innerText) < min) {
          // Below min: revert the display and skip onSubmit, same as handleBlur. Deferring the
          // onlyNumber setInitialValue call (below) until the committing branch keeps this
          // revert from being clobbered by a stale-but-already-queued state update.
          textRef.current.innerText = initialValue || defaultText;
        } else {
          if (onlyNumber) {
            setInitialValue(parseFloat(textRef.current.innerText).toString());
          }

          await onSubmit(textRef.current!.innerText);
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = e.clipboardData?.getData('text');

    if (textRef.current) {
      // get current cursor position
      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);
      const start = range?.startOffset || 0;
      const end = range?.endOffset || 0;

      // replace only the selected text
      const text = textRef.current.innerText;
      textRef.current.innerText = text.slice(0, start) + data + text.slice(end);

      // set the cursor position after the pasted text
      range?.setStart(textRef.current.firstChild!, start + data.length);
      range?.setEnd(textRef.current.firstChild!, start + data.length);
    }
  };

  const childrenWithProps = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child) && index === 0) {
      if (textRef.current) {
        return React.cloneElement(child, {
          // @ts-ignore
          ref: textRef,
          defaultValue: textRef.current.innerText !== defaultText,
        });
      } else {
        // @ts-ignore
        return React.cloneElement(child, { ref: textRef });
      }
    }

    return child;
  });

  return (
    <>
      <div
        className={styles['editable']}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={handleBlur}
        onClick={handleClick}
        onPaste={handlePaste}
        onMouseDown={(e) => e.stopPropagation()}
        className={className}
      >
        {childrenWithProps}
      </div>
      {!!errorMessage && <div className={styles['error']}>{errorMessage}</div>}
    </>
  );
};
